import { Avatar, Divider, HoverCard, Stack, Text } from '@mantine/core';
import { IconBuilding, IconCheck, IconUser } from '@tabler/icons-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getNameAbbr, UserInfo } from '@mygpt/app';

import classes from './AvatarMenu.module.css';

export interface AvatarMenuItem {
  key: string;
  label: string;
  onClick: () => void;
  color?: 'default' | 'danger';
  icon?: React.ReactNode;
  showBadge?: boolean;
}

export interface OrganizationSwitchConfig {
  enabled: boolean;
  companyId: string;
  companyName: string;
  currentScope: string | null;
  onScopeChange: (scope: string) => void;
}

export interface AvatarMenuProps {
  userInfo: UserInfo;
  menuItems: AvatarMenuItem[];
  organizationSwitch?: OrganizationSwitchConfig;
  lang?: string;
  showBadge?: boolean;
}

/**
 * 共享的用户头像菜单组件
 * 用于admin、web、website的统一用户头像和菜单显示
 */
export const AvatarMenu = ({ userInfo, menuItems, organizationSwitch, lang, showBadge }: AvatarMenuProps) => {
  const { t } = useTranslation('', { lng: lang || '' });

  const userName = useMemo(() => {
    return userInfo?.name || userInfo?.email || '';
  }, [userInfo]);

  const getNameShort = () => {
    if (!userName) return '';
    return getNameAbbr(userName);
  };

  const currentScopeValue = useMemo(() => {
    if (!organizationSwitch?.enabled) return 'personal';
    return organizationSwitch.currentScope || 'personal';
  }, [organizationSwitch]);

  const handleScopeChange = (value: string) => {
    if (!organizationSwitch?.enabled) return;
    organizationSwitch.onScopeChange(value);
  };

  const scopeOptions = useMemo(
    () => [
      { label: t('mode.personal', '我的空间'), value: 'personal', icon: <IconUser size={15} /> },
      {
        label: organizationSwitch?.companyName || t('mode.organization', '组织'),
        value: organizationSwitch?.companyId || '',
        icon: <IconBuilding size={15} />,
      },
    ],
    [organizationSwitch, t]
  );

  // 常规项和危险项（注销）分组
  const regularItems = menuItems.filter((item) => item.color !== 'danger');
  const dangerItems = menuItems.filter((item) => item.color === 'danger');

  if (!userInfo) {
    return <Avatar radius="xl" />;
  }

  return (
    <HoverCard>
      <HoverCard.Target>
        <section className="px-0 cursor-pointer" style={{ position: 'relative' }}>
          <Avatar radius="xl" title={userName} src={userInfo.picture}>
            <span className={classes.avatar}>{getNameShort()}</span>
          </Avatar>
          {showBadge && <span className={classes.badgeDot} />}
        </section>
      </HoverCard.Target>
      <HoverCard.Dropdown className={classes.hoverDropdown}>
        <Stack gap={0}>
          {/* 账号信息 */}
          <div className={classes.emailHeader}>
            <Text size="sm" c="dark.7" fw={500}>
              {userInfo?.email}
            </Text>
          </div>

          {/* 组织切换（Cloud版有组织用户显示） */}
          {organizationSwitch?.enabled && (
            <>
              {scopeOptions.map((option) => (
                <div
                  key={option.value}
                  className={classes.scopeOption}
                  data-active={currentScopeValue === option.value ? 'true' : 'false'}
                  onClick={() => handleScopeChange(option.value)}
                >
                  <span className={classes.scopeIcon}>{option.icon}</span>
                  <span className={classes.scopeLabel}>{option.label}</span>
                  {currentScopeValue === option.value && <IconCheck size={14} className={classes.scopeCheck} />}
                </div>
              ))}
              <Divider my="xs" />
            </>
          )}

          {/* 常规菜单项 */}
          {regularItems.map((item) => (
            <div key={item.key} className={classes.menuItem} onClick={item.onClick}>
              {item.icon && <span className={classes.menuItemIcon}>{item.icon}</span>}
              <span>{item.label}</span>
              {item.showBadge && <span className={classes.menuItemBadge} />}
            </div>
          ))}

          {/* 危险操作项（注销等） */}
          {dangerItems.length > 0 && (
            <>
              <Divider my={4} />
              {dangerItems.map((item) => (
                <div key={item.key} className={`${classes.menuItem} ${classes.menuItemDanger}`} onClick={item.onClick}>
                  {item.icon && <span className={classes.menuItemIcon}>{item.icon}</span>}
                  <span>{item.label}</span>
                </div>
              ))}
            </>
          )}
        </Stack>
      </HoverCard.Dropdown>
    </HoverCard>
  );
};
